/** Bounded local evidence, never a retry queue. Only verified scopes can restore it. */
import * as z from 'zod'
import type { AttemptRecord } from './insightsAttempt'

const key = 'testfeed.insights.diagnostics.v1'
const maxRecords = 20
const ttl = 60 * 60_000
const identifier = z
  .string()
  .max(120)
  .regex(/^[a-zA-Z0-9_.:/-]+$/)
const phase = z.enum(['bridge_ready', 'id_token', 'request', 'response_parse', 'scope_validation', 'render'])
const recordSchema = z.object({
  attemptId: z.string().uuid(),
  occurredAt: z.string().datetime(),
  phase,
  elapsedMs: z.number().nonnegative(),
  outcome: z.enum(['pending', 'success', 'error', 'timeout', 'cancelled']),
  status: z.number().int().min(100).max(599).optional(),
  failureClass: z
    .enum(['timeout', 'cancelled', 'network', 'http', 'json_decode', 'scope', 'render_contract'])
    .optional(),
  issueCode: identifier.optional(),
  issuePath: identifier.optional(),
  contentType: identifier.optional(),
  requestId: identifier.optional(),
  phases: z.array(z.object({ phase, elapsedMs: z.number().nonnegative() })).max(20),
  delivery: z.enum(['pending', 'intake_acknowledged', 'transport_failed']).optional(),
})
const storedSchema = z.object({
  partition: z.string().regex(/^[a-f0-9]{64}$/),
  records: z.array(recordSchema).max(maxRecords),
})
let activeScope: string | undefined
let partition: string | undefined
let records: AttemptRecord[] = []
let initialization = Promise.resolve()
let generation = 0

export function isCurrentInsightsEvidence(record: Pick<AttemptRecord, 'occurredAt'>) {
  const age = Date.now() - Date.parse(record.occurredAt)
  return age >= 0 && age < ttl
}
function storageFailure() {
  console.error('[insights-load]', { code: 'DIAGNOSTIC_STORAGE_FAILED' })
}
function persist() {
  records = records.filter(isCurrentInsightsEvidence).slice(-maxRecords)
  if (!partition) return
  try {
    sessionStorage.setItem(key, JSON.stringify({ partition, records }))
  } catch {
    console.error('[insights-load]', { code: 'DIAGNOSTIC_STORAGE_FAILED' })
  }
}
export function initializeInsightsEvidence(scope: string | undefined, resetHistory?: () => void) {
  if (activeScope === scope) return initialization
  resetHistory?.()
  const currentGeneration = ++generation
  activeScope = scope
  partition = undefined
  records = []
  if (!scope) return (initialization = Promise.resolve())
  initialization = (async () => {
    try {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(scope))
      if (activeScope !== scope || generation !== currentGeneration) return
      partition = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
      const raw = sessionStorage.getItem(key)
      if (raw && raw.length > 64_000) {
        sessionStorage.removeItem(key)
        storageFailure()
        return
      }
      if (!raw) {
        if (records.length) persist()
        return
      }
      const parsed = storedSchema.safeParse(JSON.parse(raw))
      if (!parsed.success || parsed.data.partition !== partition) {
        sessionStorage.removeItem(key)
        if (records.length) persist()
        return
      }
      const merged = new Map(
        [...parsed.data.records.filter(isCurrentInsightsEvidence), ...records].map((record) => [
          record.attemptId + record.phase,
          record,
        ]),
      )
      records = [...merged.values()].slice(-maxRecords)
      persist()
      if (records.length) console.info('[insights-load-restored]', getRetainedInsightsEvidence())
    } catch {
      console.error('[insights-load]', { code: 'DIAGNOSTIC_STORAGE_FAILED' })
    }
  })()
  return initialization
}
export function retainInsightsEvidence(scope: string, record: AttemptRecord) {
  if (activeScope !== scope) return
  const parsed = recordSchema.safeParse(record)
  if (!parsed.success) {
    storageFailure()
    return
  }
  // Delivery state updates replace one record; they never consume additional slots.
  records = records.filter((value) => value.attemptId !== record.attemptId || value.phase !== record.phase)
  records.push(parsed.data)
  persist()
}
export function updateInsightsDelivery(scope: string, attemptId: string, delivery: AttemptRecord['delivery']) {
  if (activeScope !== scope) return
  records = records.map((record) => (record.attemptId === attemptId ? { ...record, delivery } : record))
  persist()
}
export function getRetainedInsightsEvidence() {
  return records
    .filter(isCurrentInsightsEvidence)
    .map((record) => ({ ...record, phases: record.phases.map((value) => ({ ...value })) }))
}
